import {
  ApiAirportType,
  ApiItemType,
  ApiSearchProductDataRequestFromJSON,
  ApiSearchProductDataResponse
} from '@ibe/api';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Props as SelectComponentsProps } from 'react-select/dist/declarations/src';
import styled from '@emotion/styled';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBed, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { GroupBase, OptionsOrGroups } from 'react-select';
import { DataTestIdProp } from '../../../../Models/DataTestIdProp';
import fallback from '../../../../_Translations/generated/geo-unit-select-async.en.t.json';
import Keys from '../../../../_Translations/generated/geo-unit-select-async.en.t.json.keys';
import useApi from '../../../Hooks/useApi';
import useTranslation from '../../../Hooks/useTranslation';
import SelectAsyncPaginate from '../../Form/SelectAsyncPaginate/SelectAsyncPaginate';
import DestinationItem, { DestinationItemCategory } from '../../../../Models/DestinationItem';

const FontawesomeWrapper = styled.div`
  > svg {
    position: relative;
    top: 0;
    margin-right: 10px;
    width 19px !important;
  }
`;

const anywhereGeoUnit: DestinationItem = {
  id: '',
  name: '',
  code: '',
  typeCode: '',
  category: DestinationItemCategory.UNSET
};

export interface ProductSelectAsyncProps extends SelectComponentsProps {
  itemType: ApiItemType;
  airportType?: ApiAirportType;
  onSelect: (item: DestinationItem | null) => void;
  onChange?: (item: unknown | null) => void;
  value?: DestinationItem | undefined;
  code?: string;
  displayUnitCode?: boolean;
  placeholder?: string;
  hideAnywhereGeoUnit?: boolean;
  noFilterAnywhereGeoUnit?: boolean;
  menuPortalTarget?: HTMLElement;
  emptySearchAllowed?: boolean;
  renderLabel?: (option: DestinationItem, searchTerm?: string) => JSX.Element;
  optionsMapper?: (data: ApiSearchProductDataResponse, searchTerm?: string) => DestinationItem[];
}

const ProductSelectAsync = (props: ProductSelectAsyncProps & DataTestIdProp): JSX.Element => {
  const api = useApi();

  const { t } = useTranslation('geo-unit-select-async', fallback, Keys);
  const {
    value,
    code,
    placeholder,
    displayUnitCode,
    onSelect,
    onChange,
    itemType,
    hideAnywhereGeoUnit,
    dataTestId,
    noFilterAnywhereGeoUnit,
    menuPortalTarget,
    emptySearchAllowed,
    renderLabel,
    optionsMapper
  } = props;

  const anywhere = useMemo((): DestinationItem => {
    return { ...anywhereGeoUnit, ...{ name: t(Keys.defaultDirection) } };
  }, [t]);

  const [selectedValue, setSelectedValue] = useState<DestinationItem | undefined | null>(value);
  const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');
  const oldSelectedValue = useRef<DestinationItem | undefined | null>(undefined);

  useEffect((): void => {
    if (!hideAnywhereGeoUnit && !value && !placeholder) {
      setSelectedValue(anywhere);
    } else {
      setSelectedValue(value);
    }
  }, [anywhere, hideAnywhereGeoUnit, value, placeholder]);

  useEffect(() => {
    setMenuIsOpen(inputValue !== '');
    if (!!selectedValue) {
      setSelected(selectedValue);
    }
  }, [inputValue]);

  const loadedOptions = async (
    search: string,
    loadedOption: OptionsOrGroups<DestinationItem, GroupBase<DestinationItem>>
  ): Promise<{ options: DestinationItem[]; hasMore: boolean }> => {
    const request = ApiSearchProductDataRequestFromJSON({
      type: 'SearchProductDataRequest',
      search,
      offset: loadedOption.length,
      limit: 100,
      itemType
    });

    const result = await api.searchProductData(request);

    let possibleOptions: DestinationItem[];

    if (optionsMapper) {
      possibleOptions = optionsMapper(result, inputValue);
    } else {
      possibleOptions = [
        ...result.geoUnits.map(geoUnit => ({
          id: geoUnit.id,
          name: geoUnit.name,
          code: geoUnit.code,
          typeCode: geoUnit.typeCode,
          category: DestinationItemCategory.GEO_UNIT
        })),
        ...result.products.map(product => ({
          id: product.id,
          name: product.description,
          code: product.description,
          typeCode: product.itemType,
          category: DestinationItemCategory.PRODUCT
        }))
      ];
    }

    if (!hideAnywhereGeoUnit && !placeholder) {
      if (noFilterAnywhereGeoUnit && possibleOptions.length > 0 && loadedOption.length === 0) {
        possibleOptions.unshift(anywhere);
      } else {
        const regExp = new RegExp(search, 'i');
        if (regExp.test(anywhere.name)) {
          possibleOptions.unshift(anywhere);
        }
      }
    }

    if (!value && code) {
      setSelectedValue(possibleOptions.find(geoUnit => geoUnit.code === code));
      oldSelectedValue.current = possibleOptions.find(geoUnit => geoUnit.code === code);
    }

    return {
      options: possibleOptions,
      hasMore: result.hasMore
    };
  };

  const getLabel = (option: DestinationItem): JSX.Element => {
    return (
      <div className="d-flex">
        {option.typeCode === ApiItemType.HOTEL ? (
          <FontawesomeWrapper>
            <FontAwesomeIcon icon={faBed} />
          </FontawesomeWrapper>
        ) : (
          <FontawesomeWrapper>
            <FontAwesomeIcon icon={faMapMarkerAlt} />
          </FontawesomeWrapper>
        )}
        <span>{displayUnitCode ? `${option.name} (${option.code})` : option.name}</span>
      </div>
    );
  };

  const getValue = (option: DestinationItem): string => {
    return option.id;
  };

  const setSelected = (item?: DestinationItem | null, skipOldValue?: boolean): void => {
    if (!skipOldValue) {
      oldSelectedValue.current = item;
    }
    setSelectedValue(item);
    onSelect(item || null);
    if (onChange) {
      onChange(item);
    }
  };

  return (
    <div className="product-select-async">
      <SelectAsyncPaginate
        dataTestId={dataTestId}
        selectedOption={selectedValue}
        loadOptions={loadedOptions}
        defaultOptions={emptySearchAllowed}
        placeholder={placeholder}
        onChange={(item: DestinationItem): void => setSelected(item)}
        getLabel={(option): JSX.Element => {
          if (renderLabel) return renderLabel(option, inputValue);
          return getLabel(option);
        }}
        getValue={(option): string => getValue(option)}
        menuPortalTarget={menuPortalTarget}
        menuIsOpen={emptySearchAllowed ? undefined : menuIsOpen}
        inputValue={inputValue}
        setInputValue={setInputValue}
        onFocus={(): void => setSelected(null, true)}
        onBlur={(): void => setSelected(oldSelectedValue.current)}
      />
    </div>
  );
};

export default ProductSelectAsync;
